import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Table, EmptyTableText } from '@commonClient2/components';
import { toastr } from 'react-redux-toastr';
import {
    DialogTitle,
    DialogContent,
    DialogContentText,
    Tooltip,
} from '@material-ui/core';
import DataSetEdit from './DataSetEdit';
import * as dataSetManagerService from '../services/api-endpoints/dataSetManager.service';
import { extractErrorMessage } from '../../services/api';
import { refreshDataviews } from '../store/appData';
import './DataSetManager.scss';

export const DATA_SET_MANAGER_ROUTE = '/dataSetManager';

// Maximum total characters for visible field pills before showing "+X more"
const MAX_TOTAL_CHARS = 80;

// Column definitions for the table
const columns = [
    {
        key: 'name',
        tKey: 'columns.dataSet.name',
        sortable: true,
        flexGrow: 2,
    },
    {
        key: 'description',
        tKey: 'columns.dataSet.description',
        sortable: true,
        flexGrow: 3,
    },
    {
        key: 'fieldsSelected',
        tKey: 'columns.dataSet.fieldsSelected',
        sortable: false,
        flexGrow: 5,
    },
];

const DataSetManager = () => {
    const { t } = useTranslation('dataSetManager');
    const dispatch = useDispatch();
    const [dataSets, setDataSets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [sidePanelOpen, setSidePanelOpen] = useState(false);
    const [sidePanelMode, setSidePanelMode] = useState('create');
    const [selectedDataSet, setSelectedDataSet] = useState(null);
    const loadingRef = useRef(false);

    /**
     * Load all datasets from the API
     */
    const loadDataSets = useCallback(async () => {
        // Prevent multiple simultaneous calls
        if (loadingRef.current) {
            return;
        }

        loadingRef.current = true;
        setLoading(true);
        try {
            const response = await dataSetManagerService.getAllDataSets();
            const transformedData = response.data.map((ds) => ({
                id: ds.id,
                name: ds.name,
                description: ds.description,
                // Store the field keys for editing (backend expects keys)
                fieldsSelected: ds.fieldsSelected.map((f) => f.key),
                // Store the field labels separately for display in the table
                fieldsSelectedLabels: ds.fieldsSelected.map((f) => f.label || f.key)
            }));
            setDataSets(transformedData);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toastr.error(t('error.loadFailed'), errorMessage);
        } finally {
            setLoading(false);
            loadingRef.current = false;
        }
    }, [t]);

    // Load datasets on component mount
    useEffect(() => {
        loadDataSets();
    }, [loadDataSets]);

    /**
     * @param {*} rowData
     */
    const handleEdit = (rowData) => {
        setSelectedDataSet(rowData);
        setSidePanelMode('edit');
        setSidePanelOpen(true);
    };

    /**
     * @param {*} rowData
     */
    const handleDuplicate = (rowData) => {
        setSelectedDataSet(rowData);
        setSidePanelMode('duplicate');
        setSidePanelOpen(true);
    };

    /**
     * @param {*} rowData
     */
    const handleDelete = (rowData) => {
        const toastrConfirmOptions = {
            onOk: async () => {
                try {
                    await dataSetManagerService.deleteDataSet(rowData.id);
                    toastr.success(t('delete.successTitle'), t('delete.successMessage', { name: rowData.name }));
                    loadDataSets(); // Reload the list
                    dispatch(refreshDataviews()); // Refresh Redux store so bid setup form has latest dataviews
                } catch (error) {
                    const errorMessage = extractErrorMessage(error);
                    toastr.error(t('error.deleteFailed'), errorMessage);
                }
            },
            onCancel: () => { },
            component: () => (
                <div>
                    <DialogTitle style={{ padding: '16px 24px' }}>
                        {t('delete.title')}
                    </DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {t('delete.contentText', { name: rowData.name })}
                        </DialogContentText>
                    </DialogContent>
                </div>
            ),
        };
        toastr.confirm('', toastrConfirmOptions);
    };

    const handleCreateNew = () => {
        setSelectedDataSet(null);
        setSidePanelMode('create');
        setSidePanelOpen(true);
    };

    /**
     * @param {*} dataSet
     * @param {string} mode
     */
    const handleSave = async (dataSet, mode) => {
        try {
            if (mode === 'create') {
                await dataSetManagerService.createDataSet(dataSet);
                toastr.success(t('save.successTitle'), t('save.createSuccessMessage', { name: dataSet.name }));
            } else if (mode === 'edit') {
                await dataSetManagerService.updateDataSet(dataSet.id, dataSet);
                toastr.success(t('save.successTitle'), t('save.updateSuccessMessage', { name: dataSet.name }));
            } else if (mode === 'duplicate') {
                await dataSetManagerService.duplicateDataSet(dataSet.sourceId, {
                    name: dataSet.name,
                    description: dataSet.description,
                    fieldsSelected: dataSet.fieldsSelected
                });
                toastr.success(t('save.successTitle'), t('save.duplicateSuccessMessage', { name: dataSet.name }));
            }
            loadDataSets(); // Reload the list
            dispatch(refreshDataviews()); // Refresh Redux store so bid setup form has latest dataviews
            setSidePanelOpen(false);
        } catch (error) {
            const errorMessage = extractErrorMessage(error);
            toastr.error(t('error.saveFailed'), errorMessage);
            throw error; // Re-throw to let the side panel handle it
        }
    };

    // Custom renderer for fieldsSelected column to display as chips/badges
    /**
     * @param {*} rowData
     */
    const renderFieldsSelected = (rowData) => {
        // Use fieldsSelectedLabels for display (shows custom labels like "EEID")
        const fields = rowData.fieldsSelectedLabels || [];
        if (fields.length === 0) return <span />;

        let totalChars = 0;
        let visibleCount = 0;

        // Calculate how many fields can fit within the character limit
        for (let i = 0; i < fields.length; i++) {
            const fieldLength = fields[i].length;
            // Account for the field length plus some padding for spacing
            if (totalChars + fieldLength <= MAX_TOTAL_CHARS) {
                totalChars += fieldLength;
                visibleCount++;
            } else {
                break;
            }
        }

        // Ensure at least 1 field is visible
        if (visibleCount === 0) {
            visibleCount = 1;
        }

        const visibleFields = fields.slice(0, visibleCount);
        const remainingFields = fields.slice(visibleCount);
        const hasMore = remainingFields.length > 0;

        return (
            <div className="fields-selected-container">
                {visibleFields.map((field, index) => (
                    <span key={index} className="field-chip">
                        {field}
                    </span>
                ))}
                {hasMore && (
                    <Tooltip
                        title={(
                            <div>
                                {remainingFields.map((field, index) => (
                                    <div key={index}>{field}</div>
                                ))}
                            </div>
                        )}
                        arrow
                        enterDelay={300}
                        placement="top"
                    >
                        <span className="field-chip field-chip-more">
                            +
                            { remainingFields.length}
                             more
                        </span>
                    </Tooltip>
                )}
            </div>
        );
    };

    return (
        <div className="dsm-full-page">
            <Table
                loading={loading}
                data={dataSets}
                columns={columns}
                customColumnRenderers={[
                    {
                        columnName: 'fieldsSelected',
                        renderCell: renderFieldsSelected
                    }
                ]}
                id="data-set-manager-table"
                renderEmpty={() => <EmptyTableText text={t('noDataSetsFound')} />}
                onEdit={(_, value) => handleEdit(value)}
                onCreate={handleCreateNew}
                createLabel={t('createNew')}
                onDuplicate={(_, value) => handleDuplicate(value)}
                onDelete={(_, value) => handleDelete(value)}
                fillAvailableHeight
                defaultSortColumnKey="name"
            />
            <DataSetEdit
                open={sidePanelOpen}
                setOpen={setSidePanelOpen}
                mode={sidePanelMode}
                dataSet={selectedDataSet}
                onSave={handleSave}
            />
        </div>
    );
};

export default DataSetManager;

